import React, { useEffect, useCallback } from "react";
import { useNavigate, Routes, Route, Navigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { jwtDecode } from "jwt-decode";
import enGB from "date-fns/locale/en-GB";

import {
  getProfile as apiGetProfile,
  refreshToken as apiRefreshToken,
  getOrganizationData as apiGetOrganizationData,
} from "./services/api";

import LoginPage from "./ui/pages/loginPage/LoginPage";
import RegisterPage from "./ui/pages/registerPage/RegisterPage";
import ForgotPasswordPage from "./ui/pages/forgotPasswordPage/ForgotPasswordPage";
import ResetPasswordPage from "./ui/pages/resetPasswordPage/ResetPasswordPage";
import AcceptInvitePage from "./ui/pages/acceptInvitePage/AcceptInvitePage";
import VerifyAccountPage from "./ui/pages/verifyAccountPage/VerifyAccountPage";
import SplashPage from "./ui/pages/splashPage/SplashPage";
import ReportsPage from "./ui/pages/reportsPage/ReportsPage";
import ClubSettingsPage from "./ui/pages/clubSettingsPage/ClubSettingsPage";
import PlayerInformationPage from "./ui/pages/playerInformationPage/PlayerInformationPage";
import AccountSettingsPage from "./ui/pages/accountSettingsPage/AccountSettingsPage";
import TeamPage from "./ui/pages/teamPage/TeamPage";
import TeamsPage from "./ui/pages/teamsPage/TeamsPage";
// import MemberPage from "./ui/pages/memberPage/MemberPage";
import MembersPage from "./ui/pages/membersPage/MembersPage";
// import EventsPage from "./ui/pages/eventsPage/EventsPage";
import EventTypesPage from "./ui/pages/eventTypesPage/EventTypesPage";
import FeeCategoriesPage from "./ui/pages/feeCategoriesPage/FeeCategoriesPage";
import EventFeesPage from "./ui/pages/eventFeesPage/EventFeesPage";
import AdjustmentReasonsPage from "./ui/pages/adjustmentReasonsPage/AdjustmentReasonsPage";
import CreateClubPage from "./ui/pages/createClubPage/CreateClubPage";
import AuthorizeGoCardlessPage from "./ui/pages/authorizeGoCardlessPage/AuthorizeGoCardlessPage";
import AuthorizeStripePage from "./ui/pages/authorizeStripePage/AuthorizeStripePage";

import AdminLoginPage from "./ui/pages/adminLoginPage/AdminLoginPage";
import AdminSelectUserPage from "./ui/pages/adminSelectUserPage/AdminSelectUserPage";

import { withState } from "./services/state";

const init = async ({ navigate, dispatch, context }) => {
  const { loaded } = context;
  if (loaded) {
    return false;
  }

  let organization = window.location.hostname.split(".")[0];
  let organizationSlug = null;

  if (organization === "my" || organization === "localhost") {
    organizationSlug = "slate";
  } else if (organization) {
    organizationSlug = organization;
  } else {
    organizationSlug = "slate";
  }

  let siteLoadedPayload = {
    type: "siteLoaded",
    loaded: true,
  };

  let organizationData = null;

  try {
    organizationData = await apiGetOrganizationData(organizationSlug);

    const existingTokensStr = localStorage.getItem("slate-tokens");

    if (existingTokensStr) {
      let existingTokens = null;
      try {
        existingTokens = JSON.parse(existingTokensStr);

        const data = await apiRefreshToken(existingTokens.refreshToken);

        if (data.accessToken) {
          siteLoadedPayload.tokens = {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
          };
          siteLoadedPayload.loggedIn = true;

          dispatch({
            type: "storeToken",
            tokens: {
              accessToken: data.accessToken,
              refreshToken: data.refreshToken,
            },
          });

          // Decode the tokens to see if there is a clubId and playerId
          let decodedAccessToken = jwtDecode(data.accessToken);

          const getProfileData = await apiGetProfile();

          siteLoadedPayload.profile = getProfileData;

          if (decodedAccessToken.playerId) {
            siteLoadedPayload.currentPlayer = getProfileData.players.find(
              (player) => player.id === decodedAccessToken.playerId,
            );
          } else {
            let eligiblePlayers = getProfileData.players.filter(
              (player) =>
                player &&
                player.memberships &&
                player.memberships.filter(
                  (_) => _.organizationId === organizationData.guid,
                ).length > 0,
            );

            if (eligiblePlayers[0]) {
              siteLoadedPayload.currentPlayer =
                eligiblePlayers &&
                eligiblePlayers.length > 0 &&
                eligiblePlayers[0];
            } else {
              siteLoadedPayload.currentPlayer = getProfileData.players[0];
            }
          }

          if (decodedAccessToken.clubId && siteLoadedPayload.currentPlayer) {
            siteLoadedPayload.currentClub =
              siteLoadedPayload.currentPlayer.memberships
                .filter((_) => _.organizationId === organizationData.guid)
                .find(
                  (membership) =>
                    membership.clubId === decodedAccessToken.clubId,
                );
          }
        }
      } catch (e) {
        console.log(e);
      }
    }
  } catch (e) {
    console.log(e);
  }

  siteLoadedPayload.organizationData = organizationData;

  if (
    !siteLoadedPayload.currentClub &&
    siteLoadedPayload.currentPlayer &&
    siteLoadedPayload.currentPlayer.memberships &&
    siteLoadedPayload.currentPlayer.memberships.filter(
      (_) => _.organizationId === organizationData.guid,
    ).length > 0
  ) {
    siteLoadedPayload.showSelectClub = true;
    dispatch(siteLoadedPayload);
  } else if (
    siteLoadedPayload.currentPlayer &&
    !siteLoadedPayload.currentClub &&
    !window.location.pathname.startsWith("/verify-account")
  ) {
    dispatch(siteLoadedPayload);
    navigate("/create-club");
  } else if (
    siteLoadedPayload.currentClub &&
    !siteLoadedPayload.currentClub.setupComplete &&
    !window.location.pathname.startsWith("/club/merchant/authorize") &&
    !window.location.pathname.startsWith("/club/stripe/authorize") &&
    !window.location.pathname.startsWith("/verify-account")
  ) {
    dispatch(siteLoadedPayload);
    navigate("/create-club");
    // } else if (
    //   siteLoadedPayload.currentClub &&
    //   !siteLoadedPayload.currentClub.goCardlessVerified &&
    //   !window.location.pathname.startsWith("/club/merchant/authorize") &&
    //   !window.location.pathname.startsWith("/verify-account")
    // ) {
    //   dispatch(siteLoadedPayload);
    //   navigate("/verify-gocardless");
  } else if (
    siteLoadedPayload.currentClub &&
    window.location.pathname === "/"
  ) {
    console.log("goToReports", siteLoadedPayload);
    navigate("/reports");
    dispatch(siteLoadedPayload);
  } else {
    dispatch(siteLoadedPayload);
  }
};

const App = ({ dispatch, context }) => {
  const navigate = useNavigate();

  const { loggedIn, loggedInAsAdmin, loaded } = context;

  const Loading = ({ children }) => (!loaded ? <>{children}</> : null);

  const Unauthorized = ({ children }) =>
    loaded && !loggedIn && !loggedInAsAdmin ? <>{children}</> : null;

  // const AuthorizedWithoutClub = ({ children }) =>
  //   loaded && loggedIn && currentClub && !currentClub.id ? <>{children}</> : null;

  // const AuthorizedWithClub = ({ children }) =>
  //   loaded && loggedIn && currentClub && currentClub.id ? <>{children}</> : null;

  const Authorized = ({ children }) =>
    loaded && loggedIn ? <>{children}</> : null;

  const AuthorizedAdmin = ({ children }) =>
    loaded && loggedInAsAdmin && !loggedIn ? <>{children}</> : null;

  // useEffect(() => {
  //   init({ dispatch, context });
  // }, []);

  const logout = useCallback(() => {
    dispatch({
      type: "clearToken",
    });
    dispatch({
      type: "clearSessionVariables",
    });
    dispatch({
      type: "clearProfile",
    });
    navigate("/");
    localStorage.removeItem("slate-tokens");
  }, [dispatch, navigate]);

  const refreshToken = useCallback(async () => {
    try {
      console.log("Attempt to refresh token");
      console.log("loggedIn", loggedIn);
      if (loggedIn) {
        const existingTokensStr = localStorage.getItem("slate-tokens");
        if (!existingTokensStr) return;

        const existingTokens = JSON.parse(existingTokensStr);
        console.log("existingTokens", existingTokens);
        const data = await apiRefreshToken(existingTokens.refreshToken);
        console.log("data", data);

        if (data.accessToken) {
          const newTokens = {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
          };

          dispatch({
            type: "storeToken",
            tokens: newTokens,
          });

          localStorage.setItem("slate-tokens", JSON.stringify(newTokens));
        } else {
          logout();
        }
      }
    } catch (error) {
      logout();
    }
  }, [dispatch, loggedIn, logout]);

  useEffect(() => {
    refreshToken(); // Initial call on mount

    const intervalId = setInterval(refreshToken, 10 * 60 * 1000); // Every 5 minutes

    // Listen for when the user comes back to the tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshToken(); // Refresh immediately when tab becomes active
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dispatch, loggedIn, refreshToken]);

  useEffect(() => {
    init({ navigate, dispatch, context });
  }, [navigate, context, dispatch, loggedIn]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <div className="App">
        <Loading>
          <Routes>
            <Route path="*" element={<SplashPage />} />
          </Routes>
        </Loading>

        <Unauthorized>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/admin" element={<AdminLoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route
              path="/password-reset/:code"
              element={<ResetPasswordPage />}
            />
            <Route path="/accept-invite/:code" element={<AcceptInvitePage />} />
            <Route
              path="/verify-account/:code"
              element={<VerifyAccountPage />}
            />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Unauthorized>

        {/* <AuthorizedWithoutClub>
              <Routes>
                <Route path="/" element={<SplashPage />} />
                <Route path="/create-club" element={<CreateClubPage />} />
                <Route path="/verify-account/:code" element={<VerifyAccountPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </AuthorizedWithoutClub> */}

        <Authorized>
          <Routes>
            <Route path="/" element={<SplashPage />} />
            <Route path="/create-club" element={<CreateClubPage />} />
            {/* <Route
                path="/verify-gocardless"
                element={<VerifyGoCardlessPage />}
              /> */}
            <Route
              path="/verify-account/:code"
              element={<VerifyAccountPage />}
            />
            <Route
              path="/club/merchant/authorize"
              element={<AuthorizeGoCardlessPage />}
            />
            <Route
              path="/club/stripe/authorize"
              element={<AuthorizeStripePage />}
            />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/club-settings" element={<ClubSettingsPage />} />
            <Route
              path="/player-information"
              element={<PlayerInformationPage />}
            />
            <Route path="/account-settings" element={<AccountSettingsPage />} />
            <Route path="/teams/:teamId" element={<TeamPage />} />
            <Route path="/teams" element={<TeamsPage />} />
            {/* <Route path="/members/:memberId" element={<MemberPage />} /> */}
            <Route path="/members" element={<MembersPage />} />
            {/* <Route path="/events" element={<EventsPage />} /> */}
            <Route path="/event-types" element={<EventTypesPage />} />
            <Route path="/fee-levels" element={<FeeCategoriesPage />} />
            <Route path="/event-fees" element={<EventFeesPage />} />
            <Route
              path="/adjustment-reasons"
              element={<AdjustmentReasonsPage />}
            />
            <Route path="*" element={<Navigate to="/reports" replace />} />
          </Routes>
        </Authorized>

        <AuthorizedAdmin>
          <Routes>
            <Route path="/select-player" element={<AdminSelectUserPage />} />
            <Route
              path="*"
              element={<Navigate to="/select-player" replace />}
            />
          </Routes>
        </AuthorizedAdmin>
      </div>
    </LocalizationProvider>
  );
};

export default withState(App);
